import { useEffect, useRef } from 'react';
import openModal from 'App/services/Modal';
import { useModalClose } from 'App/components/Modals/Modals';
import history from 'App/pages/routes/history';
import EventBus from 'App/services/EventBus';
import StateRouter from 'App/services/StateRouter';

type UnsavedChangesGuardOptions = {
	/** Return true when there are unsaved changes that should block navigation/close */
	isDirty: () => boolean;
	/**
	 * Save handler for the alert's "Save" choice. Must settle once the save has genuinely
	 * finished. If it rejects, or the guard is still armed and dirty afterwards (saves that
	 * swallow their errors), the user stays on the page with the guard intact.
	 */
	onSave?: () => Promise<unknown> | void;
	/** Side effects for the alert's "Discard" choice (the hook always disarms the guard) */
	onDiscard?: () => void;
	/**
	 * Set false when onSave performs the close/navigation itself on success (e.g. saves that
	 * redirect to an overview page), so the guard doesn't also resume the blocked navigation.
	 */
	resumeAfterSave?: boolean;
	/**
	 * When the guarded view is a modal, pass its modalId to guard the modal close instead of
	 * the route navigations.
	 */
	modalId?: number;
	/** Closes the modal after the user discards their changes (modal views only) */
	closeModal?: () => void;
};

type WarningOutcome = 'saved' | 'discarded' | 'stay';

const activeGuards = new Set<() => boolean>();

/** For leaving the app without a navigation (a full-page redirect), which none of the guards see. */
export const hasUnsavedChanges = () => [...activeGuards].some(guardActive => guardActive());

/**
 * Guards a dirty view against losing unsaved changes, asking the user to save or discard via
 * an UnsavedChangesAlert before letting a navigation or modal close through. Route views get
 * both an Angular ($stateChangeStart) and a React Router (history.block) guard; modal views
 * (modalId set) get a modals.close guard.
 *
 * The guard state lives in refs and the options are re-read on every render, so the guards
 * never act on stale closures — the bug class behind WEB-7173 and WEB-7225.
 */
const useUnsavedChangesGuard = ({
	isDirty,
	onSave,
	onDiscard,
	resumeAfterSave = true,
	modalId,
	closeModal
}: UnsavedChangesGuardOptions) => {
	const allowStateChange = useRef(false);
	const warningOpen = useRef(false);
	const optionsRef = useRef({ isDirty, onSave, onDiscard, closeModal });
	optionsRef.current = { isDirty, onSave, onDiscard, closeModal };

	const disarm = () => {
		allowStateChange.current = true;
	};

	const guardActive = () => !allowStateChange.current && optionsRef.current.isDirty();

	useEffect(() => {
		activeGuards.add(guardActive);
		return () => {
			activeGuards.delete(guardActive);
		};
	}, []);

	// Shared between the guards (mutexed via warningOpen) so a single navigation that
	// triggers more than one guard can only ever open one alert.
	const showWarning = (): Promise<WarningOutcome> =>
		new Promise(resolve => {
			if (warningOpen.current) {
				resolve('stay');
				return;
			}
			warningOpen.current = true;
			openModal('UnsavedChangesAlert', {
				onClose: async (confirmed?: boolean) => {
					warningOpen.current = false;
					if (confirmed === undefined) {
						// Dismissed without choosing — stay on the page, keep the guard armed
						resolve('stay');
						return;
					}
					if (confirmed) {
						try {
							await optionsRef.current.onSave?.();
						} catch {
							resolve('stay');
							return;
						}
						// A save that swallowed its error leaves the guard armed and dirty —
						// stay on the page instead of navigating away and losing the changes
						resolve(guardActive() ? 'stay' : 'saved');
						return;
					}
					disarm();
					optionsRef.current.onDiscard?.();
					resolve('discarded');
				}
			});
		});

	const shouldResume = (outcome: WarningOutcome) =>
		outcome === 'discarded' || (outcome === 'saved' && resumeAfterSave);

	// Modal guard — owns closes broadcast via modals.close (incl. those triggered by navigation)
	useModalClose(
		modalId ?? -1,
		event => {
			if (modalId === undefined || !guardActive()) {
				return;
			}
			event.preventDefault();
			// eslint-disable-next-line promise/catch-or-return
			showWarning().then(outcome => {
				if (outcome === 'stay') {
					return;
				}
				if (outcome === 'discarded') {
					optionsRef.current.closeModal?.();
				}
				// If a navigation triggered the close, resume it
				if (event.state) {
					event.resumeState();
				}
			});
		},
		[]
	);

	useEffect(() => {
		if (modalId !== undefined) {
			return;
		}

		// Angular router guard — owns navigations triggered via StateRouter.go
		const listener = EventBus.on(
			'$stateChangeStart',
			async (
				event: any,
				toState: any,
				toStateParams: any,
				fromState: any,
				fromStateParams: any,
				options: any
			) => {
				if (!guardActive()) {
					return;
				}
				event.preventDefault();
				const outcome = await showWarning();
				if (shouldResume(outcome)) {
					disarm();
					StateRouter.go(toState.name, toStateParams, { ...options, location: true });
				}
			}
		);

		// React router guard — owns navigations triggered via history.push/replace/goBack
		const unblock = history.block((location, action) => {
			if (!guardActive()) {
				return undefined;
			}
			// eslint-disable-next-line promise/catch-or-return
			showWarning().then(outcome => {
				if (!shouldResume(outcome)) {
					return;
				}
				disarm();
				unblock();
				if (action === 'PUSH') {
					history.push(location);
				} else if (action === 'REPLACE') {
					history.replace(location);
				} else {
					history.goBack();
				}
			});
			return false;
		});

		return () => {
			listener();
			unblock();
		};
	}, []);

	return { disarm };
};

export default useUnsavedChangesGuard;
